import { Badge, Tooltip } from "@mantine/core";
import type { MantineColor } from "@mantine/core";
import { humanize } from "@/lib/format";

/**
 * Maps the many entity `state` strings across domains to a stable colour.
 * Greens = settled/healthy terminal; reds = failure/terminal-bad; yellow =
 * in-flight/awaiting; gray = inert/neutral. Unknown states render gray so a new
 * backend state never crashes the UI.
 */
const STATE_COLORS: Record<string, MantineColor> = {
  // healthy / success terminals
  active: "green",
  enabled: "green",
  completed: "green",
  captured: "green",
  approved: "green",
  settled: "green",
  minted: "green",
  included: "green",
  verified: "green",
  done: "green",
  signed: "green",
  // ramp saga terminals: the on-ramp's "money is in the user's Main and booked", and the
  // off-ramp's "money reached Rain's route on Base".
  credited: "green",
  delivered: "green",

  // in-flight / transient
  pending: "yellow",
  awaitingProvider: "yellow",
  toPay: "orange",
  assigned: "blue",
  verifying: "blue",
  needsReview: "orange",
  pendingAddress: "yellow",
  pendingIssuance: "yellow",
  pendingReview: "yellow",
  processing: "blue",
  burning: "blue",
  burned: "blue",
  attested: "blue",
  relaying: "blue",
  minting: "blue",
  submitted: "blue",
  requested: "blue",
  initiated: "blue",
  started: "blue",
  accruing: "blue",
  scheduled: "blue",
  // cross-chain in-flight
  detected: "blue",
  bridging: "blue",
  depositing: "blue",
  deposited: "blue",
  committed: "yellow",
  requiresResubmit: "orange",
  refundPending: "orange",
  // ramp saga in-flight
  creating: "yellow",
  deploying: "blue",
  sweeping: "blue",
  swept: "blue",
  // wallet lifecycle
  provisioned: "green",
  pendingProvisioning: "yellow",
  provisioning: "yellow",
  // the source of an in-flight migration: user-initiated actions are refused until it ends
  migrating: "orange",
  // wallet migration steps (backend-driven) and the owner-waiting ones
  provisioningNew: "blue",
  drainingPositions: "blue",
  snapshotting: "blue",
  installingTransferKeys: "yellow",
  transferringFunds: "blue",
  rekeyingEntities: "blue",
  // a transfer leg proven not executed: waits for the owner's retry (or an operator abandon)
  transferStalled: "orange",
  cleanedUp: "green",
  // upgrade offers: live (the owner may start / has started), then consumed or withdrawn
  offered: "blue",
  accepted: "teal",
  consumed: "green",
  withdrawn: "gray",

  // benign terminal / neutral
  revoked: "gray",
  released: "gray",
  // funds fully returned (cross-chain bounce-back / settlement refund) — closed, neutral
  refunded: "gray",
  expired: "gray",
  migrated: "gray",
  // an abandoned migration's successor (or a reactivation's C): kept and observed, out of selection
  retired: "gray",
  closed: "gray",
  replaced: "gray",
  inactive: "gray",
  cancelled: "gray",
  voided: "gray",
  notStarted: "gray",
  // A sweep whose balance was under the floor. Terminal, and deliberately NOT red: the funds
  // are safe on the forwarder and the next deposit carries them. Colouring it as a failure
  // would send operators chasing an incident that isn't one.
  abandoned: "gray",
  // No off-ramp route has been requested for this payment account yet.
  none: "gray",
  // A backup removed from recovery. Metadata records the removal; retained database
  // history/backups may still contain encrypted material until retention expires.
  deleted: "gray",

  // expected-but-actionable outcomes — the user must do something, but nothing broke
  denied: "orange",
  suspended: "orange",
  // An owner-key backup that no longer protects the live key: the owner key was rotated, or a
  // raw key was imported over it. Orange, not red — the row is still the only recoverable copy
  // of the PREDECESSOR's key, so it is a prompt to re-enrol, not a failure.
  superseded: "orange",

  // failure / attention
  failed: "red",
  declined: "red",
  reversed: "red",
  rejected: "red",
  reverted: "red",
  terminated: "red",
  issuanceFailed: "red",
  locked: "red",
  frozen: "grape",
};

export function stateColor(state: string | null | undefined): MantineColor {
  if (!state) return "gray";
  return STATE_COLORS[state] ?? "gray";
}

export function StatusBadge({
  state,
  title,
}: {
  state: string | null | undefined;
  title?: string;
}) {
  const badge = (
    <Badge color={stateColor(state)} variant="light" radius="sm" tt="none">
      {humanize(state)}
    </Badge>
  );
  return title ? <Tooltip label={title}>{badge}</Tooltip> : badge;
}
